import { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Displays, DisplaysValues } from '../type';
import { LayoutContext } from '../context/LayoutContext';
import { Brawler } from './Brawler';
import { Team } from './Team';
import {
  Draft,
  GameMode,
  Settings,
  TeamColors,
  TeamColorsValues,
  TeamIdx,
  Team as TeamType,
  arrayRandomValue,
  Pick,
  PickMode,
} from '../common.type';
import { MatchMap } from './MatchMap';
import { MapImg } from './MapImg';
import { ThemeColors } from '../theme';
import { copy } from '../helpers/object';
import { ContextCSS } from './Context';

const Match = styled.div<{ $displayMode: DisplaysValues }>`
  display: flex;
  flex-direction: row;
`;

const Brawlers = styled.div<{ $displayMode: DisplaysValues }>`
  ${ContextCSS}
  flex: 1;
  flex-wrap: wrap;
  gap: 2vmin;
  padding: 2vmin;
  overflow-x: scroll;
  max-height: ${(props) =>
    props.$displayMode === Displays.LANDSCAPE ? '35vmin' : '60vmin'};
`;

const Maps = styled.div<{ $displayMode: DisplaysValues }>`
  ${ContextCSS}
  max-height: ${(props) =>
    props.$displayMode === Displays.LANDSCAPE ? '60vmin' : '90vmin'};
`;

const MapModes = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  list-style: none;
  align-items: center;
  gap: 2vmin;
  padding: 1vmin 2vmin 0 2vmin;
`;
const MapMode = styled.li`
  margin: 0;
  padding: 0;
  display: flex;
`;
const MapModeButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 1vmin;
`;
const MapModeImg = styled.img`
  width: ${(props) => `4${props.theme.unit}`};
  height: ${(props) => `4${props.theme.unit}`};
  object-fit: contain;
`;
const MapList = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 2vmin;
  overflow-x: scroll;
`;
const MapListMode = styled.div<{ color: ThemeColors }>`
  display: flex;
  flex-direction: row;
  gap: 2vmin;
  padding: 2vmin;
  background: ${(props) => props.theme.color[props.color]};
`;

const Versus = styled.div<{ $displayMode: DisplaysValues }>`
  align-content: center;
  text-align: center;
  font-weight: 900;
  font-size: ${(props) => `6${props.theme.unit}`};
  color: #fff;
  text-shadow: ${(props) => `0.5${props.theme.unit}`}
    ${(props) => `0.8${props.theme.unit}`} #000;
  letter-spacing: ${(props) => `-1.5${props.theme.unit}`};
  z-index: 1;
  margin-left: ${(props) => `-1.5${props.theme.unit}`};
  pointer-events: none;
`;

type TeamsProps = {
  blueTeam: TeamType;
  redTeam: TeamType;
  settings: Settings;
  mapId: number | null;
  updateTeams: (teams: { blueTeam: TeamType; redTeam: TeamType }) => void;
  updateMapId: (mapId: number) => void;
  draftLocked?: Draft;
  draft?: Draft;
};

export const Teams = ({
  blueTeam,
  redTeam,
  settings,
  mapId,
  updateTeams,
  updateMapId,
  draftLocked,
}: TeamsProps) => {
  const { displayMode, brawlers, maps } = useContext(LayoutContext);
  const [pick, setPick] = useState<Pick | undefined>(undefined);

  const [pickMap, setPickMap] = useState<boolean>(false);

  const getTeam = useCallback(
    (color: TeamColorsValues) => {
      if (color === TeamColors.BLUE) return blueTeam;
      return redTeam;
    },
    [blueTeam, redTeam]
  );

  const select = useCallback(
    (pick: Pick, id: number) => {
      const team = getTeam(pick.color);
      const t = { ...team };
      if (pick.mode === 'brawler') {
        const brawler = brawlers.find((b) => b.id === id);
        if (!brawler || !brawler.starPowers[0] || !brawler.gadgets[0]) return;
        t[pick.index].id = brawler.id;
        t[pick.index].starPower = brawler.starPowers[0].id;
        t[pick.index].gadget = brawler.gadgets[0].id;
      }
      if (pick.mode === 'ban') {
        t[pick.index].ban = id;
      }
      if (pick.color === TeamColors.BLUE) updateTeams({ blueTeam: t, redTeam });
      if (pick.color === TeamColors.RED) updateTeams({ blueTeam, redTeam: t });
    },
    [getTeam, updateTeams, redTeam, blueTeam]
  );

  const scrollTo = useCallback((selector: string) => {
    const section = document.querySelector(selector);
    if (!section) return;
    section.scrollIntoView({
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (maps.length && mapId === null) {
      const random = arrayRandomValue(maps);
      if (random) {
        updateMapId(random.id);
      }
    }
  }, [maps]);

  useEffect(() => {
    if (!draftLocked) return;

    updateTeams({
      blueTeam: copy(draftLocked.blueTeam),
      redTeam: copy(draftLocked.redTeam),
    });
  }, [draftLocked]);

  return (
    <>
      <MatchMap
        id={mapId}
        locked={draftLocked?.mapId}
        onClick={() => {
          if (!pickMap && pick) {
            setPick(undefined);
          }
          setPickMap(!pickMap);
          if (mapId) {
            setTimeout(() => {
              scrollTo(`#map-${mapId.toString()}`);
            });
          }
        }}
      ></MatchMap>
      <Match $displayMode={displayMode}>
        <Team
          color={'blue'}
          locked={draftLocked?.blueTeam}
          team={blueTeam}
          coin={settings.coin === TeamColors.BLUE}
          mode={settings.mode}
          pick={pick}
          brawlers={brawlers}
          onClick={(index: TeamIdx, mode: PickMode) => {
            if (
              pick?.color === TeamColors.BLUE &&
              pick.index === index &&
              pick.mode === mode
            ) {
              setPick(undefined);
              return;
            }
            if (pickMap) {
              setPickMap(false);
            }
            setPick({ color: TeamColors.BLUE, index, mode });
          }}
        ></Team>
        <Versus $displayMode={displayMode}>VS</Versus>
        <Team
          color={TeamColors.RED}
          team={redTeam}
          locked={draftLocked?.redTeam}
          coin={settings.coin === TeamColors.RED}
          mode={settings.mode}
          pick={pick}
          brawlers={brawlers}
          onClick={(index: TeamIdx, mode: PickMode) => {
            if (
              pick?.color === TeamColors.RED &&
              pick.index === index &&
              pick.mode === mode
            ) {
              setPick(undefined);
              return;
            }
            if (pickMap) {
              setPickMap(false);
            }
            setPick({ color: TeamColors.RED, index, mode });
          }}
        ></Team>
      </Match>
      {pick && (
        <Brawlers $displayMode={displayMode}>
          {brawlers.map((brawler) => {
            const blueTeamValues = Object.values(blueTeam);
            const redTeamValues = Object.values(redTeam);
            const blueBrawler =
              blueTeamValues.findIndex((b) => b.id === brawler.id) !== -1;
            const redBrawler =
              redTeamValues.findIndex((b) => b.id === brawler.id) !== -1;
            const blueBan =
              blueTeamValues.findIndex((b) => b.ban === brawler.id) !== -1;
            const redBan =
              redTeamValues.findIndex((b) => b.ban === brawler.id) !== -1;
            const color =
              blueBrawler || blueBan
                ? TeamColors.BLUE
                : redBrawler || redBan
                  ? TeamColors.RED
                  : undefined;
            const disabled =
              blueBrawler || redBrawler
                ? 'brawler'
                : (pick.mode === 'ban' &&
                      ((pick.color === TeamColors.BLUE && blueBan) ||
                        (pick.color === TeamColors.RED && redBan))) ||
                    (pick.mode === 'brawler' && (blueBan || redBan))
                  ? 'ban'
                  : undefined;
            return (
              <Brawler
                mode="draft"
                key={brawler.id}
                color={color}
                brawler={brawler}
                disabled={disabled}
                border={!!disabled}
                onClick={() => {
                  select(pick, brawler.id);
                  setPick(undefined);
                }}
              ></Brawler>
            );
          })}
        </Brawlers>
      )}
      {pickMap && (
        <Maps $displayMode={displayMode}>
          <MapModes>
            {Object.values(GameMode)
              .sort()
              .map((mode) => (
                <MapMode key={mode}>
                  <MapModeButton
                    onClick={() => {
                      scrollTo(`#map-list-mode-${mode}`);
                    }}
                  >
                    <MapModeImg
                      src={`${process.env.PUBLIC_URL}/images/game-modes/${mode}.webp`}
                      alt=""
                    />
                  </MapModeButton>
                </MapMode>
              ))}
          </MapModes>
          <MapList>
            {Object.values(GameMode)
              .sort()
              .map((mode) => {
                return (
                  <MapListMode
                    key={mode}
                    id={`map-list-mode-${mode}`}
                    color={mode}
                  >
                    {maps
                      .filter((map) => map.mode === mode)
                      .sort((a, b) =>
                        a.name < b.name ? -1 : a.name > b.name ? 1 : 0
                      )
                      .map((map) => (
                        <MapImg
                          key={map.id}
                          map={map}
                          selected={map.id === mapId}
                          onClick={(id) => {
                            updateMapId(id);
                            setPickMap(false);
                          }}
                        />
                      ))}
                  </MapListMode>
                );
              })}
          </MapList>
        </Maps>
      )}
    </>
  );
};
